import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { FileBarChart, Download, FileSpreadsheet, Printer } from 'lucide-react';
import api from '../services/api';
import BranchSelect from '../components/BranchSelect';

const REPORT_KEYS = [
  ['vehicles', 'Vehicle Report'], ['drivers', 'Driver Report'],
  ['bill_book_usage', 'Bill Book Usage'], ['number_consumption', 'Number Consumption'],
  ['branch_lr_usage', 'Branch-wise LR Usage'], ['branch_invoice_usage', 'Branch-wise Invoice Usage'],
  ['pending_requests', 'Pending Bill Book Requests'], ['completed_books', 'Completed Bill Books'],
];

export default function FleetReportsPage() {
  const [report, setReport] = useState('vehicles');
  const [filters, setFilters] = useState({ date_from: '', date_to: '', branch_id: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');

  const params = useCallback(() => {
    const p = { report };
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.branch_id) p.branch_id = filters.branch_id;
    return p;
  }, [report, filters]);

  useEffect(() => {
    setLoading(true);
    api.get('/reports/fleet-branch', { params: params() })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, [params]);

  const doExport = async (format) => {
    setExporting(format);
    try {
      const r = await api.get('/reports/fleet-branch', { params: { ...params(), format: format === 'print' ? 'pdf' : format }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf' }));
      if (format === 'print') {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = `${report}.${format === 'excel' ? 'xlsx' : 'pdf'}`; a.click();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch { toast.error('Export failed'); }
    finally { setExporting(''); }
  };

  return (
    <div data-testid="fleet-reports-page" className="space-y-5">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2"><FileBarChart className="w-6 h-6 text-purple-600" /> Fleet & Branch Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Vehicles, drivers, bill book usage and branch-wise consumption. Trip histories are on each vehicle/driver page.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {REPORT_KEYS.map(([k, label]) => (
          <button key={k} onClick={() => setReport(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${report === k ? 'bg-purple-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-purple-50'}`}
            data-testid={`report-tab-${k}`}>{label}</button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div><label className="form-label">From</label><input type="date" className="form-input" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="report-date-from" /></div>
        <div><label className="form-label">To</label><input type="date" className="form-input" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="report-date-to" /></div>
        <div><label className="form-label">Branch</label><BranchSelect allowAll value={filters.branch_id} onChange={v => setFilters(f => ({ ...f, branch_id: v }))} className="w-44" testId="report-branch-filter" /></div>
        <div className="flex gap-2 ml-auto">
          <button onClick={() => doExport('pdf')} disabled={!!exporting} className="btn-secondary flex items-center gap-1.5 text-sm" data-testid="report-export-pdf"><Download className="w-4 h-4" />{exporting === 'pdf' ? '...' : 'PDF'}</button>
          <button onClick={() => doExport('excel')} disabled={!!exporting} className="btn-secondary flex items-center gap-1.5 text-sm" data-testid="report-export-excel"><FileSpreadsheet className="w-4 h-4" />{exporting === 'excel' ? '...' : 'Excel'}</button>
          <button onClick={() => doExport('print')} disabled={!!exporting} className="btn-secondary flex items-center gap-1.5 text-sm" data-testid="report-print"><Printer className="w-4 h-4" />{exporting === 'print' ? '...' : 'Print'}</button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto">
        <div className="px-4 pt-4 pb-1 flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200" data-testid="report-title">{data?.title || ''}</h3>
          <span className="text-xs text-slate-400">{data?.rows?.length ?? 0} rows</span>
        </div>
        <table className="w-full data-table" data-testid="report-table">
          <thead><tr>{(data?.columns || []).map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={(data?.columns || []).length || 1} className="text-center py-10 text-slate-400">Loading...</td></tr>
            : (data?.rows || []).length === 0 ? <tr><td colSpan={(data?.columns || []).length || 1} className="text-center py-10 text-slate-400">No data for this report.</td></tr>
            : data.rows.map((r, i) => (
              <tr key={i}>
                {data.columns.map(c => <td key={c.key} className="text-xs">{String(r[c.key] ?? '—')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
